/**
 * DryResolvePanel.jsx
 * ===========================================================================
 * Phase 4c Minimum — Item 6c.
 *
 * BC-facing "what will the resolver do for this line?" sandbox mounted on the
 * BookWorksheet header, directly beneath BookVoiceStatsStrip. Exists to give
 * the BC a way to sanity-check the Phase 4c Blocker-2 fallback chain WITHOUT
 * firing a real ElevenLabs render:
 *
 *   (1) Enter Speaker + Emotion (free text) for a single line, OR paste a
 *       tab/newline-delimited batch.
 *   (2) Click "Dry Resolve" → panel POSTs to /api/voice-rendering/dry-resolve.
 *   (3) Results render one row per line, showing:
 *         - resolved strategy + resolutionPath (exact/category/…)
 *         - matched Character + Variation
 *         - cloneVoiceId (truncated) when present
 *         - unresolved reason when no match
 *
 * The panel is intentionally collapsed by default — the strip is the
 * BC-facing summary; this is a diagnostic drawer. It opens on user
 * interaction and remembers its state across remounts via the slice.
 *
 * Dependencies:
 *   - voiceRenderingSlice (thunks: fetchBookRenderSnapshot, dryResolveLines;
 *     reducers: clearDryResolve; selectors: selectBookRenderSnapshot et al).
 *   - selectActiveScriptVersion from voiceStatsSlice — reuse the BC's
 *     <ScriptVersionSelector> pick so dry-resolve matches what the strip
 *     shows.
 *
 * Scope deliberately narrow — no preset editor, no alias hop visualization,
 * no voice audition. Those land in Phase 4d (item 8 of the minimum plan).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
  fetchBookRenderSnapshot,
  dryResolveLines,
  clearDryResolve,
  selectBookRenderSnapshot,
  selectBookRenderScriptLoaded,
  selectBookDryResolve,
  selectBookRenderLoading,
  selectBookRenderError
} from '../../slices/voiceRenderingSlice';
import { selectActiveScriptVersion } from '../../slices/voiceStatsSlice';

import './DryResolvePanel.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Parse the multi-line input textarea into an array of { speaker, emotion }.
 * Accepts tab-separated (preferred) or 2+ spaces as the speaker/emotion
 * divider. Blank/comment (#) lines are skipped.
 */
function parseLines(raw) {
  if (!raw) return [];
  const rows = [];
  const lines = String(raw).split(/\r?\n/);
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith('#')) continue;
    // tab wins; fallback to 2+ spaces as divider.
    let speaker;
    let emotion;
    if (trimmed.includes('\t')) {
      const parts = trimmed.split('\t');
      speaker = (parts[0] || '').trim();
      emotion = (parts[1] || '').trim();
    } else {
      const m = trimmed.match(/^(.+?)\s{2,}(.+)$/);
      if (m) {
        speaker = m[1].trim();
        emotion = m[2].trim();
      } else {
        speaker = trimmed;
        emotion = '';
      }
    }
    if (speaker) rows.push({ speaker, emotion });
  }
  return rows;
}

function truncate(s, n = 12) {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n)}…` : s;
}

function scriptVersionLabel(sv) {
  if (!sv) return null;
  if (sv.version !== undefined && sv.version !== null) {
    return `v${sv.version}${sv.fileName ? ` (${sv.fileName})` : ''}`;
  }
  return sv.fileName || sv.uuid || null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function DryResolvePanel({ bookId, className = '', defaultOpen = false }) {
  const dispatch = useDispatch();

  const snapshot = useSelector((s) => selectBookRenderSnapshot(s, bookId));
  const scriptLoaded = useSelector((s) => selectBookRenderScriptLoaded(s, bookId));
  const resolve = useSelector((s) => selectBookDryResolve(s, bookId));
  const loading = useSelector((s) => selectBookRenderLoading(s, bookId));
  const error = useSelector((s) => selectBookRenderError(s, bookId));
  const activeScriptVersion = useSelector((s) => selectActiveScriptVersion(s, bookId));

  const [open, setOpen] = useState(!!defaultOpen);
  const [speaker, setSpeaker] = useState('');
  const [emotion, setEmotion] = useState('');
  const [batch, setBatch] = useState('');
  const [mode, setMode] = useState('single'); // 'single' | 'batch'

  // Kick the snapshot when the panel opens so the header can show
  // character/variation counts immediately. TTL-gated by the thunk so
  // reopening won't pound Neo4j.
  useEffect(() => {
    if (!open || !bookId) return;
    dispatch(
      fetchBookRenderSnapshot({
        bookId,
        scriptVersion: activeScriptVersion?.uuid || activeScriptVersion?.version
      })
    );
  }, [open, bookId, activeScriptVersion, dispatch]);

  const parsedBatch = useMemo(() => parseLines(batch), [batch]);

  const canResolve = useMemo(() => {
    if (!bookId) return false;
    if (loading.resolve) return false;
    if (mode === 'batch') return parsedBatch.length > 0;
    return !!speaker.trim();
  }, [bookId, loading.resolve, mode, parsedBatch, speaker]);

  const handleResolve = useCallback(() => {
    if (!canResolve) return;
    const scriptVersion =
      activeScriptVersion?.uuid || activeScriptVersion?.version || undefined;
    if (mode === 'batch') {
      dispatch(
        dryResolveLines({
          bookId,
          lines: parsedBatch,
          scriptVersion
        })
      );
    } else {
      dispatch(
        dryResolveLines({
          bookId,
          speaker: speaker.trim(),
          emotion: emotion.trim(),
          scriptVersion
        })
      );
    }
  }, [canResolve, activeScriptVersion, mode, dispatch, bookId, parsedBatch, speaker, emotion]);

  const handleClear = useCallback(() => {
    dispatch(clearDryResolve({ bookId }));
  }, [dispatch, bookId]);

  // --- render -------------------------------------------------------------

  const snapshotHeader = snapshot ? (
    <div className="drp-snapshot">
      <span>
        <strong>{snapshot.characterCount ?? 0}</strong> characters
      </span>
      <span>
        <strong>{snapshot.variationCount ?? 0}</strong> variations
      </span>
      <span>
        <strong>{snapshot.exactIndexSize ?? 0}</strong> exact keys
      </span>
      <span>
        <strong>{snapshot.categoryIndexSize ?? 0}</strong> category keys
      </span>
      <span className={scriptLoaded ? 'drp-pill drp-pill-ok' : 'drp-pill drp-pill-warn'}>
        {scriptLoaded ? 'script loaded' : 'static fallback'}
      </span>
      {activeScriptVersion && (
        <span className="drp-pill drp-pill-info" title="ScriptVersionSelector pick">
          {scriptVersionLabel(activeScriptVersion)}
        </span>
      )}
    </div>
  ) : null;

  const results = resolve?.results || [];

  return (
    <div className={`drp-root ${className}`} data-book={bookId || ''}>
      <button
        type="button"
        className="drp-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="drp-toggle-chevron">{open ? '▾' : '▸'}</span>
        <span className="drp-toggle-label">Dry Resolve</span>
        <span className="drp-toggle-sub">
          {open ? 'Close resolver sandbox' : 'Test (speaker, emotion) → render plan'}
        </span>
      </button>

      {open && (
        <div className="drp-body">
          {/* Header — snapshot summary --------------------------------- */}
          <div className="drp-header">
            {snapshotHeader}
            {loading.snapshot && <span className="drp-hint">loading snapshot…</span>}
            {error.snapshot && (
              <span className="drp-err" title={error.snapshot}>
                snapshot: {error.snapshot}
              </span>
            )}
          </div>

          {/* Input ---------------------------------------------------- */}
          <div className="drp-mode">
            <label className="drp-radio">
              <input
                type="radio"
                name={`drp-mode-${bookId}`}
                checked={mode === 'single'}
                onChange={() => setMode('single')}
              />
              Single line
            </label>
            <label className="drp-radio">
              <input
                type="radio"
                name={`drp-mode-${bookId}`}
                checked={mode === 'batch'}
                onChange={() => setMode('batch')}
              />
              Batch (Speaker&lt;tab&gt;Emotion, one per row)
            </label>
          </div>

          {mode === 'single' ? (
            <div className="drp-form">
              <input
                type="text"
                className="drp-input"
                placeholder="Speaker (e.g. Landrow Marble)"
                value={speaker}
                onChange={(e) => setSpeaker(e.target.value)}
                disabled={loading.resolve}
              />
              <input
                type="text"
                className="drp-input"
                placeholder="Emotion (e.g. admiring)"
                value={emotion}
                onChange={(e) => setEmotion(e.target.value)}
                disabled={loading.resolve}
              />
            </div>
          ) : (
            <textarea
              className="drp-batch"
              rows={4}
              placeholder={'Landrow Marble\tadmiring\nNarrator\tneutral'}
              value={batch}
              onChange={(e) => setBatch(e.target.value)}
              disabled={loading.resolve}
            />
          )}

          <div className="drp-actions">
            <button
              type="button"
              className="drp-btn drp-btn-primary"
              onClick={handleResolve}
              disabled={!canResolve}
            >
              {loading.resolve ? 'Resolving…' : 'Dry Resolve'}
            </button>
            <button
              type="button"
              className="drp-btn drp-btn-ghost"
              onClick={handleClear}
              disabled={loading.resolve || results.length === 0}
            >
              Clear results
            </button>
            {mode === 'batch' && (
              <span className="drp-hint">
                {parsedBatch.length} line{parsedBatch.length === 1 ? '' : 's'} parsed
              </span>
            )}
          </div>

          {error.resolve && (
            <div className="drp-err-block">dry-resolve: {error.resolve}</div>
          )}

          {/* Results --------------------------------------------------- */}
          {results.length > 0 && (
            <div className="drp-results">
              <div className="drp-results-header">
                <span>Speaker</span>
                <span>Emotion</span>
                <span>Path</span>
                <span>Strategy</span>
                <span>Variation</span>
                <span>Voice</span>
                <span>Tag</span>
                <span>Notes</span>
              </div>
              {results.map((row, idx) => {
                const u = row.unresolved;
                return (
                  <div
                    key={row.id || `${row.speaker}-${row.emotion}-${idx}`}
                    className={`drp-row ${u ? 'drp-row-unresolved' : ''}`}
                  >
                    <span>{row.speaker || '—'}</span>
                    <span>{row.emotion || '—'}</span>
                    <span>{u ? `✗ ${row.reason}` : row.resolutionPath}</span>
                    <span>{u ? '—' : row.strategy}</span>
                    <span title={row.variationUuid || ''}>
                      {u ? '—' : (row.variationName || truncate(row.variationUuid, 10))}
                    </span>
                    <span title={row.cloneVoiceId || ''}>
                      {u ? '—' : truncate(row.cloneVoiceId, 10)}
                    </span>
                    <span title={row.renderTemplate || ''}>
                      {u ? '—' : (row.renderTemplate || '{text}')}
                    </span>
                    <span className="drp-row-notes" title={row.notes || ''}>
                      {row.notes || ''}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

DryResolvePanel.propTypes = {
  bookId: PropTypes.string,
  className: PropTypes.string,
  defaultOpen: PropTypes.bool
};

export default DryResolvePanel;
